import React from "react"
import { DeleteOutlined, PlusOutlined, StarFilled, StarOutlined, SwapOutlined } from "@ant-design/icons"
import { ConfigProvider, Image, notification,Popconfirm, Spin, Tooltip, Typography, Upload } from "antd"
import { API_BASE_MEDIA_URL } from "src/config"
import { usePointPhotos } from "src/pages-lib/points/model/use-point-photos"
import styled from "styled-components"

const { Text } = Typography

const ACCEPT = "image/jpeg,image/jpg,image/png,image/webp,image/heic"
const MAX_SIZE_MB = 10

/**
 * Изображения торговой точки.
 *
 * Живут отдельно от формы карточки: загрузка, замена и удаление применяются сразу,
 * без «Сохранить» — файл уезжает в S3 в момент выбора.
 */
export function PhotosGallery({ pointId, disabled = false }) {
  const { photos, isLoading, upload, replace, remove, setMain } = usePointPhotos(pointId)

  function handleBeforeUpload(file, photoId = null) {
    if (file.size > MAX_SIZE_MB * 1024 * 1024) {
      notification.error({ message: `Размер изображения не должен превышать ${MAX_SIZE_MB} МБ` })

      return Upload.LIST_IGNORE
    }

    if (photoId === null) {
      upload(file)
    } else {
      replace(photoId, file)
    }

    // Загрузку делаем сами, антовский uploader не задействуем.
    return false
  }

  return (
    // Карточка целиком уходит в disabled в режимах просмотра и правки только времени,
    // но кнопки галереи там и должны быть выключены — поэтому управляем этим явно.
    <ConfigProvider componentDisabled={false}>
      <Wrapper>
        <Header>
          <Text strong>Изображения точки</Text>
          <Text type="secondary">Сохраняются сразу, отдельно от кнопки «Сохранить»</Text>
        </Header>
        <Spin spinning={isLoading}>
          <List>
            <Image.PreviewGroup>
              {photos.map((photo) => (
                <Card key={photo.id}>
                  <Preview>
                    <Image src={getPhotoUrl(photo)} alt={photo.fileName ?? ""} />
                  </Preview>
                  <Labels>
                    {photo.isMain && <Label>Основное</Label>}
                    {!photo.file && (
                      <Tooltip title="Фото приехало из CRM. Удалённое здесь вернётся при следующей синхронизации">
                        <Label $muted>из CRM</Label>
                      </Tooltip>
                    )}
                    {photo.file && photo.fileName && (
                      <Tooltip title="Своё изображение поверх фото из CRM. Синхронизация его не заменит и не продублирует">
                        <Label $muted>поверх CRM</Label>
                      </Tooltip>
                    )}
                    {!photo.isActive && <Label $muted>Скрыто</Label>}
                  </Labels>
                  <Actions>
                    <ActionUpload
                      accept={ACCEPT}
                      showUploadList={false}
                      disabled={disabled}
                      beforeUpload={(file) => handleBeforeUpload(file, photo.id)}
                    >
                      <Tooltip title="Заменить изображение">
                        <ActionButton type="button" disabled={disabled}>
                          <SwapOutlined />
                        </ActionButton>
                      </Tooltip>
                    </ActionUpload>
                    <Tooltip title={photo.isMain ? "Основное изображение" : "Сделать основным"}>
                      <ActionButton
                        type="button"
                        disabled={disabled || photo.isMain}
                        onClick={() => setMain(photo.id)}
                      >
                        {photo.isMain ? <StarFilled /> : <StarOutlined />}
                      </ActionButton>
                    </Tooltip>
                    <Popconfirm
                      title="Удалить изображение?"
                      description="Файл будет удалён из хранилища безвозвратно"
                      okText="Удалить"
                      cancelText="Отмена"
                      okButtonProps={{ danger: true }}
                      disabled={disabled}
                      onConfirm={() => remove(photo.id)}
                    >
                      <Tooltip title="Удалить изображение">
                        <ActionButton type="button" $danger disabled={disabled}>
                          <DeleteOutlined />
                        </ActionButton>
                      </Tooltip>
                    </Popconfirm>
                  </Actions>
                </Card>
              ))}
            </Image.PreviewGroup>
            <Upload
              accept={ACCEPT}
              showUploadList={false}
              disabled={disabled}
              beforeUpload={(file) => handleBeforeUpload(file)}
            >
              <AddCard $disabled={disabled}>
                <PlusOutlined />
                <span>Загрузить</span>
              </AddCard>
            </Upload>
          </List>
        </Spin>
      </Wrapper>
    </ConfigProvider>
  )
}

/**
 * У своих фото ссылку собираем из файла, как в остальных формах админки,
 * у фото из CRM бэк отдаёт готовую ссылку на их CDN.
 */
function getPhotoUrl(photo) {
  return photo.file
    ? `${API_BASE_MEDIA_URL}${photo.file.path}/${photo.file.systemName}`
    : photo.link
}

const Wrapper = styled.div`
  margin-bottom: 24px;
`

const Header = styled.div`
  display: flex;
  align-items: baseline;
  gap: 12px;
  margin-bottom: 8px;
`

const List = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 12px;
`

const Card = styled.div`
  position: relative;
  width: 160px;
  height: 160px;
  border: 1px solid #d9d9d9;
  border-radius: 8px;
  overflow: hidden;
  background: #fafafa;
`

const Preview = styled.div`
  width: 100%;
  height: 100%;

  .ant-image {
    width: 100%;
    height: 100%;
  }

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`

const Labels = styled.div`
  position: absolute;
  top: 6px;
  left: 6px;
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`

const Label = styled.span`
  padding: 1px 6px;
  border-radius: 4px;
  font-size: 11px;
  line-height: 16px;
  color: #fff;
  background: ${({ $muted }) => ($muted ? "rgba(0, 0, 0, 0.45)" : "#62bb46")};
`

const Actions = styled.div`
  position: absolute;
  right: 0;
  bottom: 0;
  left: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 4px;
  padding: 4px;
  background: rgba(0, 0, 0, 0.45);
`

/*
 * Обёртки антовского аплоадера — инлайновые, из-за строчного бокса кнопка
 * «Заменить» вставала выше соседних. Раскладываем их флексом, чтобы все три
 * кнопки стояли на одном уровне.
 */
const ActionUpload = styled(Upload)`
  display: flex;

  .ant-upload.ant-upload-select,
  .ant-upload.ant-upload-select > .ant-upload {
    display: flex;
  }
`

const ActionButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: ${({ $danger }) => ($danger ? "#ff7875" : "#fff")};
  cursor: pointer;

  &:hover:not(:disabled) {
    background: rgba(255, 255, 255, 0.2);
  }

  &:disabled {
    color: rgba(255, 255, 255, 0.35);
    cursor: not-allowed;
  }
`

const AddCard = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 8px;
  width: 160px;
  height: 160px;
  border: 1px dashed #d9d9d9;
  border-radius: 8px;
  background: #fafafa;
  color: ${({ $disabled }) => ($disabled ? "rgba(0, 0, 0, 0.25)" : "rgba(0, 0, 0, 0.65)")};
  cursor: ${({ $disabled }) => ($disabled ? "not-allowed" : "pointer")};

  &:hover {
    border-color: ${({ $disabled }) => ($disabled ? "#d9d9d9" : "#62bb46")};
  }
`
